import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { isCuratedTimezone, TIMEZONES_BR } from '@/lib/timezones-br'

import { DayChipToggle, type WeekDay } from './DayChipToggle'
import { TimeRangeInput } from './TimeRangeInput'
import { TimezoneSelect } from './TimezoneSelect'

export interface BusinessHoursValue {
  timezone: string
  start: string // HH:mm
  end: string   // HH:mm
  days: WeekDay[]
}

export interface BusinessHoursEditorProps {
  value: BusinessHoursValue
  onChange: (value: BusinessHoursValue) => void
  onValidityChange?: (valid: boolean, errors: string[]) => void
  disabled?: boolean
  initialValue?: BusinessHoursValue
}

const WEEKDAY_ORDER: ReadonlyArray<{ key: WeekDay; label: string; longLabel: string }> = [
  { key: 'mon', label: 'Seg', longLabel: 'Segunda' },
  { key: 'tue', label: 'Ter', longLabel: 'Terça' },
  { key: 'wed', label: 'Qua', longLabel: 'Quarta' },
  { key: 'thu', label: 'Qui', longLabel: 'Quinta' },
  { key: 'fri', label: 'Sex', longLabel: 'Sexta' },
  { key: 'sat', label: 'Sáb', longLabel: 'Sábado' },
  { key: 'sun', label: 'Dom', longLabel: 'Domingo' },
]

/**
 * Story 3.16.1 — custom editor para `business_hours` setting.
 *
 * Substitui textarea JSON crú por 3 controles structured (chips + time
 * pickers + TZ select) + preview do efeito + JSON escape hatch colapsado.
 *
 * Componente controlado puro: parent (SettingEditor) owns o draft state e
 * lifecycle de mutation. Este componente apenas orquestra os molecules e
 * dispara `onChange` quando user interage. `onValidityChange` opcional
 * sinaliza ao parent quando o valor é inválido (parent decide bloquear
 * submit).
 *
 * Backend contract: shape `{ timezone, start, end, days }` preservado
 * exatamente — mesmo formato que `isBusinessHours` validator espera
 * (packages/admin-schemas/src/system-settings.ts:159). Zero mudança no
 * payload PATCH /admin/system-settings/business_hours.
 */
export function BusinessHoursEditor({
  value,
  onChange,
  onValidityChange,
  disabled = false,
  initialValue,
}: BusinessHoursEditorProps): React.ReactElement {
  const [jsonExpanded, setJsonExpanded] = useState<boolean>(false)
  const [jsonDraft, setJsonDraft] = useState<string>('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  // Sync jsonDraft com value quando o user NÃO está editando manualmente
  useEffect(() => {
    if (!jsonExpanded) {
      setJsonDraft(JSON.stringify(value, null, 2))
      setJsonError(null)
    }
  }, [value, jsonExpanded])

  const errors = useMemo(() => validateBusinessHours(value), [value])
  const valid = errors.length === 0

  useEffect(() => {
    onValidityChange?.(valid, errors)
  }, [valid, errors, onValidityChange])

  function toggleDay(day: WeekDay): void {
    const next = value.days.includes(day)
      ? value.days.filter((d) => d !== day)
      : [...value.days, day]
    onChange({ ...value, days: sortDays(next) })
  }

  function handleTimeRange(next: { start: string; end: string }): void {
    onChange({ ...value, start: next.start, end: next.end })
  }

  function handleTimezone(timezone: string): void {
    onChange({ ...value, timezone })
  }

  function handleApplyJson(): void {
    try {
      const parsed = JSON.parse(jsonDraft) as unknown
      if (!isShapeBusinessHours(parsed)) {
        setJsonError('JSON precisa ter shape { timezone, start, end, days[] }.')
        return
      }
      onChange(parsed)
      setJsonError(null)
    } catch (err) {
      setJsonError(`JSON inválido: ${(err as Error).message}`)
    }
  }

  function handleResetJson(): void {
    setJsonDraft(JSON.stringify(value, null, 2))
    setJsonError(null)
  }

  const summary = useMemo(
    () => computeSummary(value, initialValue),
    [value, initialValue],
  )
  const timezoneLabel = useMemo(() => {
    if (!isCuratedTimezone(value.timezone)) return value.timezone
    return (
      TIMEZONES_BR.find((tz) => tz.value === value.timezone)?.label.replace(/^🇧🇷\s/, '') ??
      value.timezone
    )
  }, [value.timezone])

  return (
    <div className="space-y-5">
      {/* ----- Dias de atendimento ----- */}
      <div className="space-y-2">
        <Label>Dias de atendimento</Label>
        <div
          role="group"
          aria-label="Dias de atendimento"
          className="flex flex-wrap gap-2"
        >
          {WEEKDAY_ORDER.map((d) => (
            <DayChipToggle
              key={d.key}
              day={d.key}
              label={d.label}
              selected={value.days.includes(d.key)}
              onToggle={() => toggleDay(d.key)}
              disabled={disabled}
            />
          ))}
        </div>
        {value.days.length === 0 && (
          <p role="alert" className="text-sm text-destructive">
            Selecione pelo menos 1 dia de atendimento.
          </p>
        )}
      </div>

      {/* ----- Horário ----- */}
      <div className="space-y-2">
        <Label className="block">Horário</Label>
        <TimeRangeInput
          start={value.start}
          end={value.end}
          onChange={handleTimeRange}
          disabled={disabled}
        />
      </div>

      {/* ----- Timezone ----- */}
      <div className="space-y-2">
        <Label htmlFor="business-hours-timezone">Fuso horário</Label>
        <TimezoneSelect
          id="business-hours-timezone"
          value={value.timezone}
          onChange={handleTimezone}
          disabled={disabled}
        />
      </div>

      {/* ----- Preview ----- */}
      {value.days.length > 0 && (
        <Card
          role="status"
          aria-live="polite"
          className="space-y-1.5 border-primary/30 bg-primary/5 p-4"
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <Info className="h-4 w-4 text-primary" aria-hidden="true" />
            Resumo da configuração
          </div>
          <p className="text-sm text-foreground">{summary.line1}</p>
          <p className="text-sm text-muted-foreground">
            Janela: {value.start || '—'} → {value.end || '—'} em {timezoneLabel || '—'}
          </p>
          {summary.delta !== null && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              ⚠️ Mudança: {summary.delta}
            </p>
          )}
          <p className="pt-1 text-xs text-muted-foreground">
            ℹ️ Workers de confirmação e reativação respeitam esta janela — mensagens fora
            deste horário são adiadas.
          </p>
        </Card>
      )}

      {/* ----- JSON escape hatch ----- */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setJsonExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded"
          aria-expanded={jsonExpanded}
          aria-controls="business-hours-json-fallback"
        >
          {jsonExpanded ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {jsonExpanded ? 'Ocultar' : 'Ver/editar'} JSON cru (para devs)
        </button>
        {jsonExpanded && (
          <div id="business-hours-json-fallback" className="space-y-2 rounded-md border bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">
              ⚠️ Editar JSON cru sobrescreve os controles acima. Use apenas se souber o
              que está fazendo.
            </p>
            <Textarea
              value={jsonDraft}
              rows={8}
              spellCheck={false}
              className={cn('font-mono text-xs', jsonError && 'border-destructive')}
              onChange={(e) => setJsonDraft(e.target.value)}
              disabled={disabled}
            />
            {jsonError !== null && (
              <p role="alert" className="text-xs text-destructive">
                {jsonError}
              </p>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleApplyJson}
                disabled={disabled}
              >
                Aplicar JSON
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleResetJson}
                disabled={disabled}
              >
                Resetar para controles
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ===========================================================================
// Helpers
// ===========================================================================

function sortDays(days: WeekDay[]): WeekDay[] {
  const order: WeekDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  return [...new Set(days)].sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

function validateBusinessHours(v: BusinessHoursValue): string[] {
  const errors: string[] = []
  if (v.days.length === 0) errors.push('Selecione pelo menos 1 dia de atendimento.')
  if (!/^\d{2}:\d{2}$/.test(v.start)) errors.push('Horário de início inválido (HH:mm).')
  if (!/^\d{2}:\d{2}$/.test(v.end)) errors.push('Horário de fim inválido (HH:mm).')
  if (v.start && v.end && v.start >= v.end) {
    errors.push('Horário de início precisa ser ANTES do fim.')
  }
  if (typeof v.timezone !== 'string' || v.timezone.length === 0) {
    errors.push('Selecione um timezone.')
  }
  return errors
}

function isShapeBusinessHours(v: unknown): v is BusinessHoursValue {
  if (v === null || typeof v !== 'object') return false
  const obj = v as Record<string, unknown>
  if (typeof obj.timezone !== 'string') return false
  if (typeof obj.start !== 'string' || !/^\d{2}:\d{2}$/.test(obj.start)) return false
  if (typeof obj.end !== 'string' || !/^\d{2}:\d{2}$/.test(obj.end)) return false
  if (!Array.isArray(obj.days)) return false
  const validDays: ReadonlySet<string> = new Set([
    'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',
  ])
  if (!obj.days.every((d) => typeof d === 'string' && validDays.has(d))) return false
  return true
}

interface Summary {
  line1: string
  delta: string | null
}

function computeSummary(value: BusinessHoursValue, initial?: BusinessHoursValue): Summary {
  const hoursPerDay = computeHoursPerDay(value.start, value.end)
  const totalHours = hoursPerDay * value.days.length

  // Special case 24/7
  const is24x7 =
    value.days.length === 7 && value.start === '00:00' && (value.end === '23:59' || value.end === '24:00')

  if (is24x7) {
    return { line1: 'Atende 24/7 (todos os dias, dia inteiro)', delta: null }
  }

  const isWeekendOnly =
    value.days.length === 2 && value.days.includes('sat') && value.days.includes('sun')

  const daysLabel = value.days
    .map((d) => WEEKDAY_ORDER.find((w) => w.key === d)?.label ?? d)
    .join(', ')

  const prefix = isWeekendOnly ? 'Apenas fim-de-semana' : `Atende ${daysLabel}`
  const line1 = `${prefix} (${value.days.length} dia${value.days.length === 1 ? '' : 's'} × ${formatHours(hoursPerDay)} = ${formatHours(totalHours)}/semana)`

  // Delta vs initial
  let delta: string | null = null
  if (initial !== undefined) {
    const initialHoursPerDay = computeHoursPerDay(initial.start, initial.end)
    const initialTotal = initialHoursPerDay * initial.days.length
    const diff = totalHours - initialTotal
    if (Math.abs(diff) >= 0.01) {
      const sign = diff > 0 ? '+' : ''
      delta = `${sign}${formatHours(diff)}/semana ${diff > 0 ? 'de cobertura' : '(reduz cobertura)'}`
    }
  }

  return { line1, delta }
}

function computeHoursPerDay(start: string, end: string): number {
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  if (
    sh === undefined || sm === undefined || eh === undefined || em === undefined ||
    Number.isNaN(sh) || Number.isNaN(sm) || Number.isNaN(eh) || Number.isNaN(em)
  ) {
    return 0
  }
  const startMin = sh * 60 + sm
  const endMin = eh * 60 + em
  if (endMin <= startMin) return 0
  return (endMin - startMin) / 60
}

function formatHours(h: number): string {
  // Render "10h" if integer, "10.5h" otherwise (1 decimal max)
  if (Math.abs(h - Math.round(h)) < 0.01) return `${Math.round(h)}h`
  return `${h.toFixed(1)}h`
}
